import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import {
  T, fmt, Card, Boton,
  Titulo, Aviso, Cargando,
} from '../lib/ui';
import { PadFirma } from './Documentos';
import { mensajeDeError } from './Gastos';

// ════════════════════════════════════════════════════════════════════
// Mi cuenta
//
// Cada persona registra aquí su firma para no dibujarla en cada
// documento. Lo que se guarda es el dibujo; el acto de firmar sigue
// pidiendo la contraseña, porque si no dejaría de ser una firma y
// sería un sello que aplica cualquiera con la sesión abierta.
// ════════════════════════════════════════════════════════════════════

export function MiCuenta({ usuario }) {
  const [d, setD] = useState(null);
  const [dibujando, setDibujando] = useState(false);
  const [aviso, setAviso] = useState(null);

  const cargar = () => api.miFirma()
    .then(setD)
    .catch((err) => { setAviso({ tipo: 'bad', texto: mensajeDeError(err) }); setD({}); });

  useEffect(() => { cargar(); }, []);

  if (!d) return <Cargando />;

  const guardar = async ({ trazo, firmanteDni }) => {
    try {
      await api.guardarFirma(trazo, firmanteDni);
      setDibujando(false);
      setAviso({ tipo: 'ok', texto: 'Tu firma quedó registrada.' });
      await cargar();
    } catch {
      setDibujando(false);
      setAviso({ tipo: 'bad', texto: 'No se pudo guardar la firma.' });
    }
  };

  const borrar = async () => {
    if (!window.confirm(
      '¿Borrar tu firma registrada?\n\n'
      + 'Los documentos que ya firmaste conservan la firma que llevaban. '
      + 'De aquí en adelante tendrás que dibujarla en cada uno.',
    )) return;
    try {
      await api.borrarFirma();
      setAviso({ tipo: 'ok', texto: 'Firma borrada.' });
      await cargar();
    } catch {
      setAviso({ tipo: 'bad', texto: 'No se pudo borrar.' });
    }
  };

  return (
    <>
      <Titulo sub={`${usuario.nombre} · ${usuario.username}`}>Mi cuenta</Titulo>

      {aviso && <Aviso tipo={aviso.tipo}>{aviso.texto}</Aviso>}

      {/* ── Firma ── */}
      <Card style={{ marginBottom: 14 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', gap: 12, marginBottom: 14,
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.navy }}>
              Mi firma
            </div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
              Para no dibujarla en cada documento
            </div>
          </div>
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '.04em',
            padding: '3px 10px', borderRadius: 11, whiteSpace: 'nowrap',
            background: d.tieneFirma ? T.okBg : T.warm,
            color: d.tieneFirma ? T.ok : T.muted,
          }}>
            {d.tieneFirma ? 'REGISTRADA' : 'SIN REGISTRAR'}
          </span>
        </div>

        {d.tieneFirma ? (
          <>
            <div style={{
              background: '#fff', border: `1px solid ${T.border}`,
              borderRadius: 8, padding: 14, textAlign: 'center',
              marginBottom: 10,
            }}>
              <img src={`data:image/png;base64,${d.firma}`} alt="Mi firma"
                   style={{ maxHeight: 90, maxWidth: '100%' }} />
              <div style={{
                borderTop: `1px solid ${T.border}`, marginTop: 10,
                paddingTop: 8, fontSize: 12, color: T.tinta,
              }}>
                {d.nombre}
                {d.dni && <span style={{ color: T.muted }}> · DNI {d.dni}</span>}
              </div>
            </div>

            <div style={{ fontSize: 11, color: T.muted, marginBottom: 12 }}>
              Registrada el {fmt.fechaHora(d.registradaEn)}
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <Boton variante="suave" onClick={() => setDibujando(true)} style={{ flex: 1 }}>
                Cambiarla
              </Boton>
              <Boton variante="suave" onClick={borrar} style={{ flex: 1 }}>
                Borrarla
              </Boton>
            </div>
          </>
        ) : (
          <>
            <div style={{
              border: `1px dashed ${T.border}`, borderRadius: 8,
              padding: '28px 16px', textAlign: 'center',
              color: T.muted, fontSize: 13, marginBottom: 12,
            }}>
              Todavía no registras tu firma.
            </div>
            <Boton variante="acento" onClick={() => setDibujando(true)}
                   style={{ width: '100%' }}>
              Registrar mi firma
            </Boton>
          </>
        )}
      </Card>

      {/* ── Lo que hay que entender ── */}
      <Card style={{ marginBottom: 14, background: T.warm }}>
        <div style={{
          fontSize: 11, color: T.muted, fontWeight: 600,
          letterSpacing: '.05em', marginBottom: 10,
        }}>
          CÓMO FUNCIONA
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.65, color: T.tinta }}>
          Guardar tu firma <strong>no hace que se estampe sola</strong>. Cada vez
          que firmes un documento la plataforma te pedirá tu contraseña.
          <br /><br />
          Si bastara con tener la sesión abierta, tu firma dejaría de ser una
          firma: sería un sello que puede aplicar cualquiera que agarre tu
          celular desbloqueado.
          <br /><br />
          Queda registrado quién firmó, cuándo, desde dónde y sobre qué versión
          del documento.
        </div>
      </Card>

      <Card>
        <div style={{
          fontSize: 11, color: T.muted, fontWeight: 600,
          letterSpacing: '.05em', marginBottom: 10,
        }}>
          DONDE NO SIRVE
        </div>
        <div style={{ fontSize: 13, lineHeight: 1.65, color: T.tinta }}>
          La <strong>planilla de movilidad</strong> exige firma manuscrita del
          trabajador sobre el papel: lo pide el Reglamento del Impuesto a la
          Renta. Esa se imprime, se firma a mano y se adjunta la foto.
          <br /><br />
          Esto tampoco es una firma digital certificada, que exige un
          certificado de entidad acreditada ante INDECOPI. Sirve como
          constancia de control interno.
        </div>
      </Card>

      {dibujando && (
        <PadFirma
          titulo={d.tieneFirma ? 'Cambiar mi firma' : 'Registrar mi firma'}
          nombreFijo={usuario.nombre}
          dniInicial={d.dni || ''}
          onGuardar={guardar}
          onCancelar={() => setDibujando(false)}
        />
      )}
    </>
  );
}
